import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpRight, CheckCircle2, Clock3, FileWarning, KeyRound,
  MessageSquareWarning, Utensils, WalletCards, WashingMachine, Wrench, X,
} from "lucide-react";
import type { DashboardSummary } from "@apartos/contracts";
import { api } from "../lib/api";
import { useDialog } from "../hooks/useDialog";
import { ModalPortal } from "./ModalPortal";

type AttentionTask = DashboardSummary["attention"][number];

const sourceLabels: Record<NonNullable<AttentionTask["source"]>, string> = {
  booking: "Бронирование",
  qr: "QR-запрос гостя",
  housekeeping: "Уборка",
  room_service: "Доставка в номер",
  apartment: "Апартамент",
};

const icons = {
  payment: WalletCards,
  document: FileWarning,
  room: KeyRound,
  cleaning: WashingMachine,
  repair: Wrench,
  request: MessageSquareWarning,
  room_service: Utensils,
} as const;

export function TaskDetailModal({ task, onClose, onOpenSection }: {
  task: AttentionTask;
  onClose: () => void;
  onOpenSection: (path: string) => void;
}) {
  const dialogRef = useDialog<HTMLElement>(onClose);
  const client = useQueryClient();
  const requestId = task.id.startsWith("request-") ? task.id.slice("request-".length) : null;
  const updateRequest = useMutation({
    mutationFn: (status: "in_progress" | "resolved") => api.updateGuestRequest(requestId!, status),
    onSuccess: async (_, status) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["dashboard"] }),
        client.invalidateQueries({ queryKey: ["guest-requests"] }),
        client.invalidateQueries({ queryKey: ["operational-shift"] }),
      ]);
      if (status === "resolved") onClose();
    },
  });
  const Icon = icons[task.kind];

  return <ModalPortal onClose={onClose}>
    <section ref={dialogRef} className="booking-modal task-detail-modal" role="dialog" aria-modal="true" aria-labelledby="task-detail-title">
      <header className="modal-header">
        <div><h2 id="task-detail-title">Задача</h2><p>{task.source ? sourceLabels[task.source] : "Операционная задача"}</p></div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Закрыть"><X size={17} /></button>
      </header>
      <div className="modal-body">
        <div className={`task-detail-modal__lead tone-${task.tone}`}>
          <span><Icon size={22} /></span>
          <div><small>{task.source === "qr" ? "ЗАПРОС ИЗ АПАРТАМЕНТА" : "ТРЕБУЕТ ДЕЙСТВИЯ"}</small><h3>{task.title}</h3><p>{task.subtitle}</p></div>
        </div>
        <dl className="task-detail-modal__facts">
          <div><dt>Статус</dt><dd>{task.status === "in_progress" ? "В работе" : "Новая"}</dd></div>
          <div><dt>Ответственный</dt><dd>{task.assignedTo || "Текущая смена"}</dd></div>
          <div><dt>Создана</dt><dd>{task.createdAt ? new Date(task.createdAt).toLocaleString("ru-RU", { dateStyle: "medium", timeStyle: "short" }) : "Сегодня"}</dd></div>
          <div><dt>Приоритет</dt><dd>{task.tone === "red" ? "Срочный" : task.tone === "orange" ? "Повышенный" : "Обычный"}</dd></div>
        </dl>
        {task.source === "qr" && <div className="task-detail-modal__note">
          <MessageSquareWarning size={17} />
          <div><strong>Источник определён автоматически</strong><span>CRM сохранила QR-код и апартамент, из которого пришёл запрос.</span></div>
        </div>}
        {updateRequest.error && <div className="form-error">{updateRequest.error.message}</div>}
      </div>
      <footer className="modal-footer">
        <button className="secondary-button" type="button" onClick={() => onOpenSection(task.path)}><ArrowUpRight size={14} /> Открыть раздел</button>
        <div className="modal-footer__spacer" />
        {requestId && task.actionable && task.status !== "in_progress" && <button className="secondary-button" type="button" disabled={updateRequest.isPending} onClick={() => updateRequest.mutate("in_progress")}><Clock3 size={14} /> В работу</button>}
        {requestId && task.actionable && <button className="primary-button" type="button" disabled={updateRequest.isPending} onClick={() => updateRequest.mutate("resolved")}><CheckCircle2 size={14} /> Выполнено</button>}
      </footer>
    </section>
  </ModalPortal>;
}
